import classNames from "classnames";
import { LitElement, css, html, type PropertyValueMap } from "lit";
import { customElement, property } from "lit/decorators.js";

// Initially no highlight, then highlight rolls to right
// Instantly transition back to "initHidden" after "hiddenAfterShown" transition plays
type showState = "initHidden" | "shown" | "hiddenAfterShown";

@customElement("bp-highlight")
export class Highlight extends LitElement {
  static styles = css`
    :host {
      --reveal-time: 1s;
      --left-gradient-color: var(--color-highlight-yellow-1);
      --right-gradient-color: var(--color-highlight-yellow-2);
    }

    .highlight {
      transition: background-position var(--reveal-time);
      background-color: none;
      background: linear-gradient(
        90deg,
        var(--left-gradient-color) 1.46%,
        var(--right-gradient-color) 50%,
        rgba(255, 255, 255, 0) 50%
      );
      background-size: 201% 100%;
      background-position: 100% 0;
    }

    .can-spotlight {
      color: var(--color-black);
      transition:
        color var(--spotlight-transition-duration),
        background var(--spotlight-transition-duration);
    }

    .highlight.shown {
      background-position: 0 0;
    }

    .highlight.hiddenAfterShown {
      background-position: -99% 0;
    }

    .highlight.pink {
      --left-gradient-color: var(--color-highlight-pink-1);
      --right-gradient-color: var(--color-highlight-pink-2);
    }

    .highlight.orange {
      --left-gradient-color: var(--color-highlight-orange-1);
      --right-gradient-color: var(--color-highlight-orange-2);
    }

    .highlight.blue {
      --left-gradient-color: var(--color-highlight-blue-1);
      --right-gradient-color: var(--color-highlight-blue-2);
    }

    .highlight.green {
      --left-gradient-color: var(--color-highlight-green-1);
      --right-gradient-color: var(--color-highlight-green-2);
    }
  `;

  @property({ attribute: "color" })
  color?: string = "yellow";

  @property({ type: Number, attribute: "reveal-time" })
  userRevealTime?: number = 1;

  @property({
    type: Boolean,
    attribute: "no-animation",
  })
  noAnimation?: boolean = false;

  @property({ type: Boolean, attribute: "can-spotlight" })
  canSpotlight?: boolean;

  @property({
    type: Boolean,
    attribute: "show",
  })
  show?: boolean = false;

  revealTime = 1; // Sometimes we override user time to 0 to allow for reset
  shouldResetState = false; // True to allow the highlight state to reset
  shownState: showState = "initHidden";
  lastState?: showState;
  resetStateTimeoutId: NodeJS.Timeout | null = null;

  resetShownState() {
    const timeoutDuration = this.revealTime * 1000 + 50;
    this.resetStateTimeoutId = setTimeout(() => {
      this.shouldResetState = true;
      this.requestUpdate();
    }, timeoutDuration);
  }

  protected willUpdate(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (changedProperties.has("show")) {
      this.shouldResetState = false; // Cancel reset when state changes again;
      if (this.resetStateTimeoutId) {
        clearTimeout(this.resetStateTimeoutId);
      }
    }
    if (this.shouldResetState) {
      this.revealTime = 0;
      this.shownState = "initHidden";
      this.shouldResetState = false;
      return;
    }

    if (this.noAnimation) {
      this.revealTime = 0;
    } else if (this.userRevealTime) {
      this.revealTime = this.userRevealTime;
    }
    this.shownState = this.show
      ? "shown"
      : this.lastState === "shown"
      ? "hiddenAfterShown"
      : "initHidden";
    if (this.shownState === "hiddenAfterShown") {
      this.resetShownState();
    }
  }

  render() {
    const classes = classNames("highlight", this.color, this.shownState, {
      "can-spotlight": this.canSpotlight,
    });
    const style = `--reveal-time: ${this.revealTime}s`;
    return html`<span class="${classes}" style="${style}"><slot></slot></span>`;
  }

  updated() {
    this.lastState = this.shownState;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-highlight": Highlight;
  }
}
